import React from "react";
import clsx from "clsx";
import type KnowledgeGraph from "@semantical/modules/knowledge-graph/KnowledgeGraph";

type PredicateInfo = {
    uri: string;
    label: string;
    count: number;
    color: string;
};

type Props = {
    knowledgeGraph: KnowledgeGraph | null;
    highlightedPredicates: Map<string, string>;
    onPredicateToggle: (predicateUri: string, color: string) => void;
    onClearAll: () => void;
    darkMode?: boolean;
};

type State = {
    expanded: boolean;
};

/**
 * Generate a deterministic color from a URI string using HSL.
 * Same URI always produces the same color.
 */
function generatePredicateColor(predicateUri: string): string {
    let hash = 0;
    for (let i = 0; i < predicateUri.length; i++) {
        hash = ((hash << 5) - hash) + predicateUri.charCodeAt(i);
        hash = hash & hash; // Convert to 32-bit integer
    }
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 65%, 55%)`;
}

/**
 * Determine if text should be white or black based on background color brightness.
 */
function getContrastColor(hslColor: string): string {
    // Parse HSL and check lightness
    const match = hslColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (match) {
        const lightness = parseInt(match[3], 10);
        return lightness > 50 ? '#000000' : '#ffffff';
    }
    return '#ffffff';
}

export default class WordCloud extends React.Component<Props, State> {
    private collapseTimeout: ReturnType<typeof setTimeout> | null = null;
    private panelRef = React.createRef<HTMLDivElement>();

    state: State = {
        expanded: false,
    };

    componentWillUnmount() {
        if (this.collapseTimeout) {
            clearTimeout(this.collapseTimeout);
        }
    }

    /**
     * Extract unique predicates from the knowledge graph and count their usage.
     */
    getPredicateStats(): PredicateInfo[] {
        const { knowledgeGraph } = this.props;
        if (!knowledgeGraph) return [];

        const predicateMap = new Map<string, { label: string; count: number }>();

        knowledgeGraph.relationships.forEach((relationship) => {
            const uri = relationship.predicate.uri;
            const label = relationship.predicate.label;
            const existing = predicateMap.get(uri);
            if (existing) {
                existing.count++;
            } else {
                predicateMap.set(uri, { label, count: 1 });
            }
        });

        return Array.from(predicateMap.entries()).map(([uri, { label, count }]) => ({
            uri,
            label,
            count,
            color: generatePredicateColor(uri),
        }));
    }

    /**
     * Calculate font size based on count, scaled between min and max.
     */
    getTagSize(count: number, minCount: number, maxCount: number): number {
        const minSize = 10;
        const maxSize = 16;
        if (maxCount === minCount) return (minSize + maxSize) / 2;
        const ratio = (count - minCount) / (maxCount - minCount);
        return minSize + ratio * (maxSize - minSize);
    }

    handleMouseEnter = () => {
        if (this.collapseTimeout) {
            clearTimeout(this.collapseTimeout);
            this.collapseTimeout = null;
        }
        this.setState({ expanded: true });
    };

    handleMouseLeave = () => {
        this.collapseTimeout = setTimeout(() => {
            this.setState({ expanded: false });
        }, 200);
    };

    handleTagClick = (predicate: PredicateInfo) => {
        this.props.onPredicateToggle(predicate.uri, predicate.color);
    };

    handleClearAll = () => {
        this.props.onClearAll();
    };

    render() {
        const { highlightedPredicates } = this.props;
        const { expanded } = this.state;

        const predicates = this.getPredicateStats();
        const counts = predicates.map(p => p.count);
        const minCount = counts.length > 0 ? Math.min(...counts) : 0;
        const maxCount = counts.length > 0 ? Math.max(...counts) : 0;

        const collapsedWidth = 66;
        const expandedWidth = 200;

        const hasHighlights = highlightedPredicates.size > 0;

        return (
            <div
                ref={this.panelRef}
                className="flex flex-col h-full bg-white/[0.98] dark:bg-surface-dark border-l border-slate-200 dark:border-accent-border font-sans text-[13px] text-slate-700 dark:text-[#e0e0e0] transition-[width] duration-200"
                style={{
                    width: expanded ? expandedWidth : collapsedWidth,
                    minWidth: expanded ? expandedWidth : collapsedWidth
                }}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
            >
                {/* Header with toggle */}
                <div className="flex items-center gap-2.5 px-2.5 py-3 border-b border-slate-200 dark:border-white/[0.06]">
                    <button
                        className="flex items-center justify-center w-[38px] h-[38px] p-0 bg-accent-teal/10 dark:bg-accent/10 border border-accent-teal/25 dark:border-accent/25 rounded-[10px] cursor-pointer text-accent-teal dark:text-accent transition-all duration-150 hover:bg-accent-teal/20 dark:hover:bg-accent/20"
                        title={expanded ? "Predicates panel" : "Expand predicates"}
                    >
                        {/* Word cloud / tag icon */}
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 9h2"/>
                            <path d="M4 15h2"/>
                            <path d="M9 4h6"/>
                            <path d="M9 20h6"/>
                            <rect x="9" y="9" width="6" height="6" rx="1"/>
                            <path d="M18 9h2"/>
                            <path d="M18 15h2"/>
                        </svg>
                    </button>
                    {expanded && (
                        <span className="text-sm font-semibold text-slate-900 dark:text-white whitespace-nowrap">Predicates</span>
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden p-2">
                    {expanded ? (
                        <>
                            {/* Clear All button - only show when there are highlights */}
                            {hasHighlights && (
                                <button
                                    className="w-full mb-2 px-2 py-1 text-[10px] font-medium bg-slate-100 dark:bg-white/[0.08] text-slate-500 dark:text-white/60 rounded hover:bg-slate-200 dark:hover:bg-white/[0.12] transition-colors"
                                    onClick={this.handleClearAll}
                                >
                                    Clear All ({highlightedPredicates.size})
                                </button>
                            )}

                            {predicates.length === 0 ? (
                                <div className="px-2 py-4 text-[11px] text-slate-400 dark:text-white/30 italic text-center">
                                    No predicates yet
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-1">
                                    {predicates.map(predicate => {
                                        const isSelected = highlightedPredicates.has(predicate.uri);
                                        const fontSize = this.getTagSize(predicate.count, minCount, maxCount);
                                        const textColor = getContrastColor(predicate.color);

                                        return (
                                            <span
                                                key={predicate.uri}
                                                className={clsx(
                                                    "inline-block px-2 py-1 rounded-md cursor-pointer transition-all duration-150",
                                                    "hover:scale-105 hover:shadow-md"
                                                )}
                                                style={{
                                                    backgroundColor: predicate.color,
                                                    fontSize: `${fontSize}px`,
                                                    color: textColor,
                                                    boxShadow: isSelected
                                                        ? `0 0 0 2px var(--color-surface-dark, white), 0 0 0 4px ${predicate.color}`
                                                        : undefined,
                                                }}
                                                onClick={() => this.handleTagClick(predicate)}
                                                title={`${predicate.label} (${predicate.count} relationship${predicate.count !== 1 ? 's' : ''})`}
                                            >
                                                {predicate.label}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    ) : (
                        /* Collapsed state - show count badge */
                        predicates.length > 0 && (
                            <div className="flex flex-col items-center gap-1 pt-2">
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-white/50">
                                    {predicates.length}
                                </span>
                            </div>
                        )
                    )}
                </div>
            </div>
        );
    }
}
